import { useState, useEffect } from 'react';

/**
 * Hook to calculate optimal dropdown position
 * @param {boolean} open - Whether dropdown is open
 * @param {HTMLElement} anchorEl - Anchor element
 * @param {number} dropdownHeight - Height of dropdown
 * @param {number} margin - Buffer space
 * @returns {Object} Position configuration for Popover
 */
export const useDropdownPosition = (open, anchorEl, dropdownHeight = 260, margin = 20) => {
    const [position, setPosition] = useState({
        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
        transformOrigin: { vertical: 'top', horizontal: 'left' },
    });

    useEffect(() => {
        if (!open || !anchorEl) return;

        const calculatePosition = () => {
            const rect = anchorEl.getBoundingClientRect();
            const viewportHeight = window.innerHeight;

            // Calculate available space
            const spaceBelow = viewportHeight - rect.bottom - margin;
            const spaceAbove = rect.top - margin;

            // Determine vertical position
            let verticalAnchor = 'bottom';
            let verticalTransform = 'top';

            if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
                verticalAnchor = 'top';
                verticalTransform = 'bottom';
            }

            // Determine horizontal position (default to left, can be extended for right positioning)
            const horizontalAnchor = 'left';
            const horizontalTransform = 'left';

            setPosition({
                anchorOrigin: {
                    vertical: verticalAnchor,
                    horizontal: horizontalAnchor,
                },
                transformOrigin: {
                    vertical: verticalTransform,
                    horizontal: horizontalTransform,
                },
            });
        };

        // Small delay to ensure anchorEl is properly positioned
        const timer = setTimeout(calculatePosition, 10);

        // Recalculate on window resize
        const handleResize = () => {
            if (open) {
                calculatePosition();
            }
        };

        window.addEventListener('resize', handleResize);
        window.addEventListener('scroll', calculatePosition, true);

        return () => {
            clearTimeout(timer);
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('scroll', calculatePosition, true);
        };
    }, [open, anchorEl, dropdownHeight, margin]);

    return position;
};
